import {
  AiConversationMessageReaction,
  IAiConversationMessageAssistant,
} from '@/types/firebase'
import { IGenericError } from '@/types/shared'
import { notify } from '@/utils/index'
import React, { FC, useCallback, useState } from 'react'
import { BiLike, BiSolidDislike, BiDislike, BiSolidLike } from 'react-icons/bi'
import { HiCheck } from 'react-icons/hi2'
import { TbCopy } from 'react-icons/tb'
import { AiAssistantMessageActionBtn } from './AiAssistantMessageActionBtn'
import { BsQuestionCircle } from 'react-icons/bs'

interface Props {
  message: IAiConversationMessageAssistant
  handleReactionChange?: (
    currentReaction: AiConversationMessageReaction | null,
  ) => Promise<void>
  handleReportIssue?: () => void
  isLoadingReportIssue?: boolean
}

export const AiAssistantMessageActions: FC<Props> = ({
  message: { id: messageId, reaction, content },
  handleReactionChange,
  handleReportIssue,
  isLoadingReportIssue,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isCopied, setIsCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      if (!navigator.clipboard) {
        throw new Error('Clipboard API not available')
      }

      const { topic, text, example, commonMistake, tip, followUpQuestion } =
        content

      let textParts: string[] = [text]

      if (topic) {
        textParts = [topic, '\n', ...textParts]
      }

      if (example) {
        textParts = [...textParts, '\n Example \n', example]

        if (commonMistake) {
          textParts = [...textParts, '\n Common Mistake \n', commonMistake]
        }

        if (tip) {
          textParts = [...textParts, '\n Tip \n', tip]
        }
      }

      if (followUpQuestion) {
        textParts = [...textParts, '\n', followUpQuestion]
      }

      const allFieldsCombined = textParts.join('\n')

      await navigator.clipboard.writeText(allFieldsCombined)
      setIsCopied(true)

      setTimeout(() => {
        setIsCopied(false)
      }, 2000)
    } catch (error: unknown) {
      console.error('error in handleCopy :>> ', {
        message: (error as IGenericError)?.message,
        error,
      })

      if (error && typeof error === 'object' && 'message' in error) {
        notify('error', error.message)
        return
      }

      notify('error', 'Failed to copy text')
    }
  }, [content])

  return (
    <ul className="flex items-center gap-2 text-v8-blue">
      {/* Report Issue */}
      <li>
        <AiAssistantMessageActionBtn
          tooltipId={`ai-assistant-report-issue-${messageId}`}
          tooltipText={'Report Issue'}
          disabled={isLoadingReportIssue}
          onClick={handleReportIssue}
        >
          <BsQuestionCircle className={'h-4 w-4'} />
        </AiAssistantMessageActionBtn>
      </li>
      {/* Copy */}
      <li>
        <AiAssistantMessageActionBtn
          tooltipId={`ai-assistant-copy-to-clipboard-${messageId}`}
          tooltipText={isCopied ? 'Copied' : 'Copy'}
          disabled={isCopied}
          onClick={handleCopy}
        >
          {isCopied ? (
            <HiCheck className="w-4 h-4" />
          ) : (
            <TbCopy className="w-4 h-4 scale-x-[-1]" />
          )}
        </AiAssistantMessageActionBtn>
      </li>
      {/* Reaction Positive */}
      <li>
        <AiAssistantMessageActionBtn
          disabled={isLoading || !handleReactionChange}
          onClick={async () => {
            if (!handleReactionChange) {
              return
            }

            const targetReaction: AiConversationMessageReaction | null =
              reaction === AiConversationMessageReaction.Positive
                ? null
                : AiConversationMessageReaction.Positive

            setIsLoading(true)

            await handleReactionChange(targetReaction)

            setIsLoading(false)
          }}
        >
          {reaction && reaction === AiConversationMessageReaction.Positive ? (
            <BiSolidLike className="w-4 h-4" />
          ) : (
            <BiLike className="w-4 h-4" />
          )}
        </AiAssistantMessageActionBtn>
      </li>
      {/* Reaction Negative */}
      <li>
        <AiAssistantMessageActionBtn
          disabled={isLoading || !handleReactionChange}
          onClick={async () => {
            if (!handleReactionChange) {
              return
            }

            const targetReaction: AiConversationMessageReaction | null =
              reaction === AiConversationMessageReaction.Negative
                ? null
                : AiConversationMessageReaction.Negative

            setIsLoading(true)

            await handleReactionChange(targetReaction)

            setIsLoading(false)
          }}
        >
          {reaction && reaction === AiConversationMessageReaction.Negative ? (
            <BiSolidDislike className="w-4 h-4" />
          ) : (
            <BiDislike className="w-4 h-4" />
          )}
        </AiAssistantMessageActionBtn>
      </li>
    </ul>
  )
}
